// The game's content as the page draws it (sprite names, names, sizes), by numeric id.
import { GameConstants, GroundType, JewelryKind, ObjectType, OffhandKind, TileEntityType, TileLayer, ToolKind, Binding, WeaponClass, enumName } from '../../../shared/src/constants';
import { content } from '../core/content';
import { Equipment, Inventory, ToolBelt, allItems, isArmor, isConsumable, isJewelry, isOffhand, isTool, jewelrySlotKind } from '../core/rules/items';
import { allRecipes } from '../core/rules/economy';
import { tileEntityDef } from '../../../shared/src/rules/world';
import { UiColors } from '../core/rules/tooltip';
import { outfitSlots } from './game';

export function contentForPage(): object {
  const c = content();
  const named: Record<string, number> = {};
  for (const [k, v] of Object.entries(GroundType)) if (typeof v === 'number') named['ground' + k] = v;
  for (const [k, v] of Object.entries(ObjectType)) if (typeof v === 'number') named['object' + k] = v;
  return {
    tileSize: GameConstants.tileSize, chunkSize: GameConstants.chunkSize, tickRate: GameConstants.tickRate,
    appearances: GameConstants.appearanceCount, maxNameLength: GameConstants.maxNameLength, maxChat: GameConstants.maxChatLength,
    maxFriends: GameConstants.maxFriends, maxIgnored: GameConstants.maxIgnored,
    bagColumns: Inventory.mainColumns, bagRows: Inventory.mainRows, protocol: GameConstants.protocolVersion,
    items: allItems().map(d => ({
      id: d.id, key: d.key, name: d.name, icon: d.icon, quality: d.quality, qualityColor: UiColors.quality(d.quality), maxStack: d.maxStack,
      tool: enumName(ToolKind, d.tool).toLowerCase(), armor: isArmor(d) ? Equipment.slotFor(d.armor) : -1, armorValue: d.armorValue, look: d.look,
      sellPrice: d.sellPrice, heals: d.heals, consumable: isConsumable(d), binding: enumName(Binding, d.binding).toLowerCase(),
      belt: isOffhand(d) ? ToolBelt.offhand : isJewelry(d) ? ToolBelt.slotFor(jewelrySlotKind(d.jewelry)) : isTool(d) ? ToolBelt.slotFor(d.tool) : -1,
      offhand: enumName(OffhandKind, d.offhand).toLowerCase(), jewelry: enumName(JewelryKind, d.jewelry).toLowerCase(), unique: d.unique,
      ...(d.weapon ? {
        weapon: {
          class: enumName(WeaponClass, d.weapon.weaponClass).toLowerCase(), damage: d.weapon.damage, reach: d.weapon.reach, arc: d.weapon.arcDegrees, cooldown: d.weapon.cooldownSeconds,
          mana: d.weapon.manaCost, ammo: d.weapon.ammo, ranged: d.weapon.projectile !== null,
        },
      } : {}),
    })),
    entities: [...c.entities.values()],
    npcs: [...c.npcs.values()].map(n => ({ id: n.id, name: n.name, appearance: n.appearance, greeting: n.greeting, vendor: n.stock.length > 0, outfit: outfitSlots(n.outfit) })),
    mobs: [...c.mobs.values()].map(m => ({ id: m.type, name: m.name, sprite: m.sprite, tint: m.tint })),
    recipes: allRecipes().map(r => ({
      id: r.id, station: enumName(TileEntityType, r.station).toLowerCase(), item: r.output.item, count: r.output.count, inputs: r.inputs.map(i => ({ ...i })),
    })),
    tiles: c.tiles.tiles.map(t => ({
      baseGround: t.baseGround || 0, id: t.id, name: t.name, layer: t.layer === TileLayer.Ground ? 'ground' : 'object', solid: t.solid, animated: t.animated,
      sprite: t.sprite, join: t.join, joinsWith: t.joinsWith,
      lightRadius: t.lightRadius, lightColor: t.lightColor, lightX: t.lightX, lightY: t.lightY, flat: t.flat, sortY: t.sortY,
    })),
    tileEntities: [TileEntityType.Workbench, TileEntityType.Npc, TileEntityType.Portal, TileEntityType.Furnace].map(type => {
      const d = tileEntityDef(type)!;
      return { id: type, key: enumName(TileEntityType, type).toLowerCase(), name: d.name, w: d.width, h: d.height, solid: d.solid, verb: d.verb };
    }),
    armorSlots: Equipment.slots.map((_, i) => Equipment.slotName(i)),
    beltSlots: ToolBelt.slots.map((_, i) => ToolBelt.slotName(i)),
    named,
  };
}
